import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { apis } from '@/modules/apiClients'
import { organizationsQueryKeys } from '@/storeHooks/useOrganizations'
import { useAuth } from './useAuth'
import { useOrganization } from './useOrganization'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'
import { PartnerService } from '@duck/api-definitions'

export const useOrganizationAdminSettings = () => {
  const { organizationId } = useAuth()
  const { organization } = useOrganization(organizationId)

  const getOrgAdminSetting = useCallback(<T extends keyof PartnerService.OrganizationAdminSettings>(settingName: T): boolean | undefined => {
    return organization?.adminSettings?.[settingName]
  }, [organization?.adminSettings])

  return {
    getOrgAdminSetting,
    isLoading: !organization,
    adminSettings: organization?.adminSettings,
  }
}

export const useOrganizationSettings = () => {
  const { organizationId } = useAuth()
  const { organization } = useOrganization(organizationId)
  const queryClient = useQueryClient()
  const { t } = useTar()

  // Overloads for getOrgSetting
  function getOrgSetting(settingName: 'invitationEmails' | 'submissionEmails'): boolean | undefined
  function getOrgSetting(settingName: 'feedbackLanguage'): PartnerService.OrganizationSettingsFeedbackLanguageEnum | undefined
  function getOrgSetting(settingName: keyof PartnerService.OrganizationSettings): boolean | PartnerService.OrganizationSettingsFeedbackLanguageEnum | undefined {
    return organization?.settings?.[settingName] as any
  }

  // Overloads for setOrgSetting
  async function setOrgSetting(settingName: 'invitationEmails' | 'submissionEmails', value: boolean): Promise<void>
  async function setOrgSetting(settingName: 'feedbackLanguage', value: PartnerService.OrganizationSettingsFeedbackLanguageEnum): Promise<void>
  async function setOrgSetting(settingName: keyof PartnerService.OrganizationSettings, value: boolean | PartnerService.OrganizationSettingsFeedbackLanguageEnum): Promise<void> {
    if (!organizationId) return

    await apis.partnerService.organizationsOrganizationIdSettingsPatch(
      organizationId,
      {
        settings: {
          [settingName]: value,
        } as PartnerService.OrganizationSettings,
      }
    )
    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: organizationsQueryKeys.all() })
    toast.success(t('organizationSettings.settingsUpdated'))
  }

  const getOrgSettingMemo = useCallback(getOrgSetting, [organization?.settings])
  const setOrgSettingMemo = useCallback(setOrgSetting, [organizationId, queryClient, t])

  return {
    getOrgSetting: getOrgSettingMemo,
    setOrgSetting: setOrgSettingMemo,
    isLoading: !organization,
    settings: organization?.settings,
  }
}
